import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  useMediaQuery
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import ClearIcon from "@mui/icons-material/Clear";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import type { Partner, PartnerService, Service } from "@kappa-v2/contracts";
import { PageHeader } from "../components/PageHeader.js";
import { DialogHelp } from "../components/DialogHelp.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { EmptyState } from "../components/EmptyState.js";
import { type RowActionItem } from "../components/DataGridRowActionsMenu.js";
import { RowQuickActions } from "../components/RowQuickActions.js";
import { useApi } from "../hooks/useApi.js";
import { formLabel } from "../lib/labels.js";
import { isRecurringService } from "../lib/payments.js";

interface FormValues {
  personId: string;
  serviceId: string;
  name: string;
  description: string;
  emailNotification: boolean;
  isDeducible: boolean;
}

interface PartnerServicesListResponse {
  data?: PartnerService[];
}

interface GroupedPartnerServices {
  partner: Partner;
  items: ResolvedPartnerService[];
}

interface ResolvedPartnerService extends PartnerService {
  partner: Partner;
  service?: Service;
  effectiveName: string;
  effectiveDescription: string;
  effectiveEmailNotification: boolean;
  effectiveIsDeducible: boolean;
  price?: number;
}

const EMPTY: FormValues = {
  personId: "",
  serviceId: "",
  name: "",
  description: "",
  emailNotification: false,
  isDeducible: false
};

function resolveList<T>(value: T[] | { data?: T[] } | undefined): T[] {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : (value.data ?? []);
}

function partnerLabel(partner: Partner): string {
  return `${partner.lastName} ${partner.firstName}`.trim();
}

function serviceLabel(service: Service): string {
  return `${service.name} (${service.price.toLocaleString("it-IT", { minimumFractionDigits: 2 })} €)`;
}

export function PartnerServicesPage() {
  const { t } = useTranslation();
  const { call } = useApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const schema = Yup.object({
    personId: Yup.string().required(t("partnerServices.form.memberRequired")),
    serviceId: Yup.string().required(t("partnerServices.form.serviceRequired")),
    name: Yup.string().trim().required(t("partnerServices.form.nameRequired")),
    description: Yup.string().trim().required(t("partnerServices.form.descriptionRequired"))
  });
  const [partners, setPartners] = useState<Partner[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [rows, setRows] = useState<PartnerService[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(
    searchParams.get("personId")
  );
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PartnerService | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ResolvedPartnerService | null>(null);

  const activePartners = useMemo(
    () => partners.filter((partner) => partner.active),
    [partners]
  );

  const recurringServices = useMemo(
    () => services.filter(isRecurringService).sort((a, b) => a.name.localeCompare(b.name, "it")),
    [services]
  );

  const partnerById = useMemo(
    () => new Map(activePartners.map((partner) => [partner._id ?? "", partner])),
    [activePartners]
  );

  const serviceById = useMemo(
    () => new Map(services.map((service) => [service._id ?? "", service])),
    [services]
  );

  const selectedPartner = selectedPartnerId ? partnerById.get(selectedPartnerId) ?? null : null;
  const selectedService = selectedServiceId ? serviceById.get(selectedServiceId) ?? null : null;

  const hydrateFormFromService = useCallback((service: Service | null | undefined): Pick<FormValues, "name" | "description" | "emailNotification" | "isDeducible"> => ({
    name: service?.name ?? "",
    description: service?.description ?? "",
    emailNotification: Boolean(service?.emailNotification),
    isDeducible: Boolean(service?.isDeducible)
  }), []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [partnerResponse, serviceResponse, partnerServiceResponse] = await Promise.all([
        call<{ data?: Partner[] }>("/api/v1/partners?active=true"),
        call<Service[] | { data?: Service[] }>("/api/v1/services"),
        call<PartnerService[] | PartnerServicesListResponse>("/api/v1/payments/partner-services")
      ]);

      setPartners(resolveList(partnerResponse));
      setServices(resolveList(serviceResponse));
      setRows(resolveList(partnerServiceResponse).filter((item) => item.active !== false));
    } catch (err) {
      const message = typeof err === "object" && err && "message" in err
        ? String((err as { message?: string }).message ?? t("partnerServices.loadError"))
        : t("partnerServices.loadError");
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [call]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const resolvedRows = useMemo<ResolvedPartnerService[]>(
    () => rows.flatMap((row) => {
      const partner = partnerById.get(row.personId);
      if (!partner) {
        return [];
      }

      const service = serviceById.get(row.serviceId);
      return [{
        ...row,
        partner,
        service,
        effectiveName: row.name?.trim() || service?.name || t("partnerServices.noName"),
        effectiveDescription: row.description?.trim() || service?.description || t("partnerServices.noDescription"),
        effectiveEmailNotification: row.emailNotification ?? service?.emailNotification ?? false,
        effectiveIsDeducible: row.isDeducible ?? service?.isDeducible ?? false,
        price: service?.price
      }];
    }),
    [rows, partnerById, serviceById]
  );

  const groupedRows = useMemo<GroupedPartnerServices[]>(() => {
    const filtered = resolvedRows.filter((row) => {
      const matchPartner = !selectedPartnerId || row.personId === selectedPartnerId;
      const matchService = !selectedServiceId || row.serviceId === selectedServiceId;
      return matchPartner && matchService;
    });

    const groups = new Map<string, GroupedPartnerServices>();
    for (const row of filtered) {
      const existing = groups.get(row.personId);
      if (existing) {
        existing.items.push(row);
      } else {
        groups.set(row.personId, {
          partner: row.partner,
          items: [row]
        });
      }
    }

    return [...groups.values()]
      .map((group) => ({
        ...group,
        items: [...group.items].sort((a, b) => a.effectiveName.localeCompare(b.effectiveName, "it"))
      }))
      .sort((a, b) => partnerLabel(a.partner).localeCompare(partnerLabel(b.partner), "it"));
  }, [resolvedRows, selectedPartnerId, selectedServiceId]);

  const formik = useFormik<FormValues>({
    initialValues: EMPTY,
    validationSchema: schema,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const service = serviceById.get(values.serviceId);
        const body: Omit<PartnerService, "_id"> = {
          personId: values.personId,
          serviceId: values.serviceId,
          active: editTarget?.active ?? true,
          name: values.name.trim(),
          description: values.description.trim(),
          emailNotification: values.emailNotification,
          isDeducible: values.isDeducible,
          serviceType: service?.serviceType
        };

        if (editTarget?._id) {
          await call(`/api/v1/payments/partner-services/${editTarget._id}`, {
            method: "PUT",
            body: { ...body, _id: editTarget._id }
          });
        } else {
          await call("/api/v1/payments/partner-services", {
            method: "POST",
            body
          });
        }

        setDialogOpen(false);
        setEditTarget(null);
        formik.resetForm({ values: EMPTY });
        await fetchAll();
      } finally {
        setSaving(false);
      }
    }
  });

  const openAdd = useCallback(() => {
    setEditTarget(null);
    formik.resetForm({
      values: {
        ...EMPTY,
        personId: selectedPartnerId ?? "",
        serviceId: selectedServiceId ?? "",
        ...hydrateFormFromService(selectedService)
      }
    });
    setDialogOpen(true);
  }, [formik, hydrateFormFromService, selectedPartnerId, selectedService, selectedServiceId]);

  const openEdit = useCallback((row: ResolvedPartnerService) => {
    setEditTarget(row);
    formik.resetForm({
      values: {
        personId: row.personId,
        serviceId: row.serviceId,
        name: row.effectiveName,
        description: row.effectiveDescription,
        emailNotification: row.effectiveEmailNotification,
        isDeducible: row.effectiveIsDeducible
      }
    });
    setDialogOpen(true);
  }, [formik]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget?._id) {
      return;
    }
    await call(`/api/v1/payments/partner-services/${deleteTarget._id}`, {
      method: "DELETE"
    });
    setDeleteTarget(null);
    await fetchAll();
  }, [call, deleteTarget, fetchAll]);

  const buildActionItems = useCallback((row: ResolvedPartnerService): RowActionItem[] => [
    {
      key: "edit",
      label: t("partnerServices.editAction"),
      description: t("partnerServices.editActionDesc"),
      icon: <EditIcon fontSize="small" />,
      onClick: () => openEdit(row)
    },
    {
      key: "delete",
      label: t("partnerServices.deleteAction"),
      description: t("partnerServices.deleteActionDesc"),
      icon: <DeleteIcon fontSize="small" />,
      onClick: () => setDeleteTarget(row),
      destructive: true,
      dividerBefore: true
    }
  ], [openEdit]);

  const currentServiceForForm = formik.values.serviceId
    ? serviceById.get(formik.values.serviceId) ?? null
    : null;

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader
        title={t("partnerServices.title")}
        subtitle={t("partnerServices.subtitle")}
        actionLabel={t("partnerServices.newAssociation")}
        actionIcon={<LinkIcon />}
        onAction={openAdd}
        onRefresh={fetchAll}
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <Box
          sx={{
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,.08)"
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Autocomplete
                options={activePartners}
                value={selectedPartner}
                onChange={(_, value) => setSelectedPartnerId(value?._id ?? null)}
                getOptionLabel={(option) => partnerLabel(option)}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                renderInput={(params) => (
                  <TextField {...params} label={t("partnerServices.socio")} placeholder={t("partnerServices.socioPlaceholder")} />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Autocomplete
                options={recurringServices}
                value={selectedService}
                onChange={(_, value) => setSelectedServiceId(value?._id ?? null)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                renderOption={(props, option) => (
                  <li {...props}>{serviceLabel(option)}</li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label={t("partnerServices.service")} placeholder={t("partnerServices.servicePlaceholder")} />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                fullWidth
                variant="text"
                startIcon={<ClearIcon />}
                onClick={() => {
                  setSelectedPartnerId(null);
                  setSelectedServiceId(null);
                }}
              >
                {t("partnerServices.clearFilters")}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {!loading && groupedRows.length === 0 ? (
          <EmptyState
            title={t("partnerServices.noneFound")}
            description={t("partnerServices.noneFoundDesc")}
            actionLabel={t("partnerServices.associateService")}
            onAction={openAdd}
          />
        ) : (
          groupedRows.map((group) => (
            <Card key={group.partner._id} sx={{ overflow: "hidden" }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {partnerLabel(group.partner)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("partnerServices.linkedServices", { count: group.items.length })}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <Chip size="small" label={formLabel(group.partner.partnerType)} variant="outlined" />
                  <Chip size="small" label={t("common.active")} color="success" variant="outlined" />
                </Stack>
              </Box>
              <Divider />
              {isMobile ? (
                <Box>
                  {group.items.map((row, idx) => (
                    <Box
                      key={row._id}
                      sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none" }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700}>
                            {row.effectiveName}
                          </Typography>
                          {row.service?.name && row.service.name !== row.effectiveName && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t("partnerServices.base", { name: row.service.name })}
                            </Typography>
                          )}
                        </Box>
                        <RowQuickActions quickKeys={["edit"]} items={buildActionItems(row)} />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {row.effectiveDescription}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", alignItems: "center" }}>
                        <Typography variant="body2" fontWeight={700}>
                          {typeof row.price === "number"
                            ? `${row.price.toLocaleString("it-IT", { minimumFractionDigits: 2 })} €`
                            : "—"}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${t("partnerServices.colNotifications")}: ${row.effectiveEmailNotification ? t("common.yes") : t("common.no")}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${t("partnerServices.colDeductible")}: ${row.effectiveIsDeducible ? t("common.yes") : t("common.no")}`}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Box>
              ) : (
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("partnerServices.service")}</TableCell>
                      <TableCell>{t("partnerServices.colDescription")}</TableCell>
                      <TableCell align="right">{t("partnerServices.colPrice")}</TableCell>
                      <TableCell align="center">{t("partnerServices.colNotifications")}</TableCell>
                      <TableCell align="center">{t("partnerServices.colDeductible")}</TableCell>
                      <TableCell align="right">{t("common.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.items.map((row) => (
                      <TableRow key={row._id} hover>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {row.effectiveName}
                          </Typography>
                          {row.service?.name && row.service.name !== row.effectiveName && (
                            <Typography variant="caption" color="text.secondary">
                              {t("partnerServices.base", { name: row.service.name })}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 260 }}>
                          <Typography variant="body2">{row.effectiveDescription}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {typeof row.price === "number"
                            ? `${row.price.toLocaleString("it-IT", { minimumFractionDigits: 2 })} €`
                            : "—"}
                        </TableCell>
                        <TableCell align="center">{row.effectiveEmailNotification ? t("common.yes") : t("common.no")}</TableCell>
                        <TableCell align="center">{row.effectiveIsDeducible ? t("common.yes") : t("common.no")}</TableCell>
                        <TableCell align="right">
                          <RowQuickActions quickKeys={["edit"]} items={buildActionItems(row)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Card>
          ))
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            {editTarget ? t("partnerServices.editAssociation") : t("partnerServices.newAssociation")}
            <DialogHelp steps={[{ title: t("help.formTitle"), description: t("help.forms.partnerServices") }]} />
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={activePartners}
                  value={formik.values.personId ? partnerById.get(formik.values.personId) ?? null : null}
                  onChange={(_, value) => formik.setFieldValue("personId", value?._id ?? "")}
                  getOptionLabel={(option) => partnerLabel(option)}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("partnerServices.socio")}
                      error={formik.touched.personId && Boolean(formik.errors.personId)}
                      helperText={formik.touched.personId ? formik.errors.personId : undefined}
                    />
                  )}
                  disabled={Boolean(editTarget)}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={recurringServices}
                  value={formik.values.serviceId ? serviceById.get(formik.values.serviceId) ?? null : null}
                  onChange={(_, value) => {
                    formik.setFieldValue("serviceId", value?._id ?? "");
                    const hydrated = hydrateFormFromService(value);
                    formik.setFieldValue("name", hydrated.name);
                    formik.setFieldValue("description", hydrated.description);
                    formik.setFieldValue("emailNotification", hydrated.emailNotification);
                    formik.setFieldValue("isDeducible", hydrated.isDeducible);
                  }}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  renderOption={(props, option) => (
                    <li {...props}>{serviceLabel(option)}</li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("partnerServices.service")}
                      error={formik.touched.serviceId && Boolean(formik.errors.serviceId)}
                      helperText={formik.touched.serviceId ? formik.errors.serviceId : undefined}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("partnerServices.overrideTitle")}
                  </Typography>
                </Divider>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("partnerServices.nameLabel")}
                  {...formik.getFieldProps("name")}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name ? formik.errors.name : undefined}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={t("partnerServices.descriptionLabel")}
                  {...formik.getFieldProps("description")}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description ? formik.errors.description : undefined}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={formik.values.emailNotification}
                      onChange={(event) => formik.setFieldValue("emailNotification", event.target.checked)}
                    />
                  )}
                  label={t("partnerServices.notifications")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={formik.values.isDeducible}
                      onChange={(event) => formik.setFieldValue("isDeducible", event.target.checked)}
                    />
                  )}
                  label={t("partnerServices.deductible")}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {t("partnerServices.listPrice", { price: currentServiceForForm
                    ? `${currentServiceForForm.price.toLocaleString("it-IT", { minimumFractionDigits: 2 })} €`
                    : "—" })}
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? t("common.saving") : t("common.save")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        message={t("partnerServices.deleteConfirm", {
          name: deleteTarget?.effectiveName ?? "",
          partner: deleteTarget ? partnerLabel(deleteTarget.partner) : t("partnerServices.thisMember")
        })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
